import { hookstate, ImmutableArray, ImmutableObject, none, State, useHookstate } from '@hookstate/core'
import { LocaleState, useLocaleState } from "./useLocaleState";
import { PackageItem, Product } from './useInventoryState';
import { devtools } from '@hookstate/devtools';
import { Catalog, CatalogState, useCatalogState } from './useCatalogState';
import axios, { AxiosRequestConfig } from 'axios';
import { AppConfigState, useAppConfigState } from './useAppConfigState';
import { SessionState, useSessionState } from './useSessionState';
import { FwQueryResponse } from '../lib/FwReact/FwQuery';
import { StorefrontSettingsState, useStorefrontSettingsState } from './useStorefrontSettingsState';

export class ShoppingCartItemModel {
    product: ImmutableObject<Product>;
    qty: number;
    packageItemQtys: { [key: string]: number } = {};
    showPackageItems: boolean = false;
    hasQtyError: boolean = false;

    constructor(product: ImmutableObject<Product>, qty: number) {
        this.product = product;
        this.qty = qty;
    }
}

const storefront_shoppingcartitems_key = 'storefront-shoppingcartitems';
const shoppingcartitemsJson = window.localStorage.getItem('storefront-shoppingcartitems');
let initialShoppingCartItems: ShoppingCartItemModel[];
if (shoppingcartitemsJson) {
    initialShoppingCartItems = JSON.parse(shoppingcartitemsJson);
} else {
    initialShoppingCartItems = [];
}
let shoppingCartItemsStateStore = hookstate<ShoppingCartItemModel[]>(initialShoppingCartItems, devtools({ key: 'ShoppingCartItemsState' }));

export class ShoppingCartMessage {
    header: string = '';
    description: string = '';
    messages: string[] = [];
}
export class ShoppingCartItemsState {
    private localeState: LocaleState;
    private shoppingCartItemsState: State<ShoppingCartItemModel[]>;
    private catalogState: CatalogState;
    private appConfigState: AppConfigState;
    private sessionState: SessionState;
    private shoppingCartMessages: State<ShoppingCartMessage[]>
    private storefrontSettings: StorefrontSettingsState;

    constructor(
        localeState: LocaleState,
        shoppingCartItemsState: State<ShoppingCartItemModel[]>,
        catalogState: CatalogState,
        appConfigState: AppConfigState,
        sessionState: SessionState,
        shoppingCartMessages: State<ShoppingCartMessage[]>,
        storefrontSettings: StorefrontSettingsState
    ) {
        this.localeState = localeState;
        this.shoppingCartItemsState = shoppingCartItemsState;
        this.catalogState = catalogState;
        this.appConfigState = appConfigState;
        this.sessionState = sessionState;
        this.shoppingCartMessages = shoppingCartMessages;
        this.storefrontSettings = storefrontSettings;
    }

    get(): ImmutableArray<ShoppingCartItemModel> {
        let allItemsInCart = this.shoppingCartItemsState.get();
        return allItemsInCart
    }

    getMessages(): ImmutableArray<ShoppingCartMessage> {
        return this.shoppingCartMessages.get();
    }

    reset(catalogId?: string) {
        if (!catalogId) {
            // delete all shopping cart items
            this.shoppingCartItemsState.set([]);
        } else {
            // delete the shopping cart items from the specified catalog
            const items = this.shoppingCartItemsState.get();
            for (let i = items.length - 1; i >= 0; i--) {
                const item = items[i];
                if (item.product.WebCatalogId === catalogId) {
                    this.shoppingCartItemsState[i].set(none);
                }
            }
        }
        this.saveToLocalStorage();
    }

    addFromProduct(product: ImmutableObject<Product>, qty: number | string): ShoppingCartItemModel {
        if (typeof qty === 'string') {
            qty = parseFloat(qty)
        }
        const item = new ShoppingCartItemModel(product, qty);
        this.add(item);
        return item;
    }

    add(item: ShoppingCartItemModel) {
        let alreadyInCart = false;
        for (let i = 0; i < this.shoppingCartItemsState.get().length; i++) {
            const item2 = this.shoppingCartItemsState.get()[i];
            if (item.product.Id === item2.product.Id) {
                alreadyInCart = true;
                this.changeQty(i, item, item2.qty + item.qty);
            }
        }
        if (!alreadyInCart) {
            this.shoppingCartItemsState.merge([item]);
            this.saveToLocalStorage();
        }
    }

    replace(items: ShoppingCartItemModel[]) {
        this.shoppingCartItemsState.set(items);
        this.saveToLocalStorage();
    }

    delete(item: ShoppingCartItemModel) {
        const shoppingCartItems = this.shoppingCartItemsState.get();
        if (shoppingCartItems) {
            const index = shoppingCartItems.findIndex(o => o.product.Id === item.product.Id);
            if (index > -1) {
                this.shoppingCartItemsState.set(p => {
                    p.splice(index, 1);
                    return p;
                });
                this.saveToLocalStorage();
            }
        }
    }

    deleteItems(items: ShoppingCartItemModel[]) {
        const shoppingCartItems = this.shoppingCartItemsState.get();
        const indexesToDelete: number[] = [];
        if (shoppingCartItems && items) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                const index = shoppingCartItems.findIndex(o => o.product.Id === item.product.Id);
                if (index > -1) {
                    indexesToDelete.push(index);
                }
            }
            if (indexesToDelete.length > 0) {
                for (let i = indexesToDelete.length - 1; i >= 0; i--) {
                    this.shoppingCartItemsState[i].set(none);
                }
                this.saveToLocalStorage();
            }
        }
    }

    deletePackageItem(shoppingCartItem: ShoppingCartItemModel, packageItem: ImmutableObject<PackageItem>) {
        this.setPackageItemQty(shoppingCartItem.product, packageItem, 0);
    }

    changeQty(index: number, item: ImmutableObject<ShoppingCartItemModel>, qty: number) {
        const shoppingCartItems = JSON.parse(JSON.stringify(this.shoppingCartItemsState.get())) as ShoppingCartItemModel[];
        if (index > -1) {
            if (qty > 0) {
                shoppingCartItems[index].qty = qty;
                this.shoppingCartItemsState.set(shoppingCartItems);
                this.saveToLocalStorage();
            } else {
                this.delete(item);
            }
        }
    }

    getQty(product: ImmutableObject<Product>): number {
        let shoppingCartItems = this.shoppingCartItemsState.get();
        for (let i = 0; i < shoppingCartItems.length; i++) {
            const item = shoppingCartItems[i];
            if (item.product.Id === product.Id) {
                return item.qty;
            }
        }
        return 0; 
    }

    setQty(product: ImmutableObject<Product>, qty: number): void {
        const productId = product.Id;
        const shoppingCartItems = JSON.parse(JSON.stringify(this.shoppingCartItemsState.get())) as ShoppingCartItemModel[];
        const shoppingCartItemIndex = shoppingCartItems.findIndex(i => i.product.Id === product.Id);
        if ((shoppingCartItemIndex !== -1) && (shoppingCartItemIndex >= 0) && (shoppingCartItemIndex < shoppingCartItems.length)) {
            const shoppingCartItem = shoppingCartItems[shoppingCartItemIndex];
            if (qty > 0) {
                shoppingCartItem.qty = qty;
                this.shoppingCartItemsState.set(shoppingCartItems);
            } else {
                this.shoppingCartItemsState.set(p => {
                    p.splice(shoppingCartItemIndex, 1);
                    return p;
                });
            }
            this.saveToLocalStorage();
        } else {
            this.addFromProduct(product, qty);
        }

        // product becomes undefined by this point (or at least gives an undefined exception)
        const shoppingCartItems2 = JSON.parse(JSON.stringify(this.shoppingCartItemsState.get())) as ShoppingCartItemModel[];
        let shoppingCartItem2 = shoppingCartItems2.find(i => i.product.Id === productId);
        if (shoppingCartItem2) {
            const packageItems = shoppingCartItem2.product.PackageItems ?? [];
            for (let i = 0; i < packageItems.length; i++) {
                const packageItem = packageItems[i];
                if (packageItem) {
                    if (shoppingCartItem2.packageItemQtys[packageItem.InventoryId] === undefined) {
                        shoppingCartItem2.packageItemQtys[packageItem.InventoryId] = 0;
                    }
                    if (qty > 0) {
                        if (!packageItem.IsOption) {
                            let piDefaultQuantity = packageItem.DefaultQuantity;
                            if (packageItem.DefaultQuantity < 1 && packageItem.DefaultQuantity > 0) {
                                shoppingCartItem2.packageItemQtys[packageItem.InventoryId] = Math.ceil(qty * piDefaultQuantity);
                            }
                            else {
                                shoppingCartItem2.packageItemQtys[packageItem.InventoryId] = qty * piDefaultQuantity;
                            }
                            this.shoppingCartItemsState.set(shoppingCartItems2);
                        }
                    } else {
                        this.shoppingCartItemsState.set(p => {
                            p.splice(i, 1);
                            return p;
                        });
                    }
                }
            }
            this.saveToLocalStorage();
        }
        
    }

    getPackageItemQty(product: ImmutableObject<Product>, packageiItem: ImmutableObject<PackageItem>): number {
        let shoppingCartItems = this.shoppingCartItemsState.get();
        const shoppingCartItem = shoppingCartItems.find(i => i.product.Id === product.Id);
        if (shoppingCartItem) {
            if (shoppingCartItem.packageItemQtys && shoppingCartItem.packageItemQtys[packageiItem.InventoryId]) {
                return shoppingCartItem.packageItemQtys[packageiItem.InventoryId];
            }
        }
        return 0;
    }

    setPackageItemQty(product: ImmutableObject<Product>, packageItem: ImmutableObject<PackageItem>, qty: number): void {
        let shoppingCartItems = JSON.parse(JSON.stringify(this.shoppingCartItemsState.get())) as ShoppingCartItemModel[];
        let shoppingCartItemIndex = shoppingCartItems.findIndex(i => i.product.Id === product.Id);
        if (shoppingCartItemIndex === -1) {
            this.setQty(product, qty);
            shoppingCartItems = JSON.parse(JSON.stringify(this.shoppingCartItemsState.get())) as ShoppingCartItemModel[];
            shoppingCartItemIndex = shoppingCartItems.findIndex(i => i.product.Id === product.Id);
        }
        // if we were able to find the main package in the shopping cart, then update the count of the packageitem
        if ((shoppingCartItemIndex !== -1) && (shoppingCartItemIndex >= 0) && (shoppingCartItemIndex < shoppingCartItems.length)) {
            const shoppingCartItem = shoppingCartItems[shoppingCartItemIndex];
            if (shoppingCartItem) {
                shoppingCartItem.packageItemQtys[packageItem.InventoryId] = qty;
                this.shoppingCartItemsState.set(shoppingCartItems);
                this.saveToLocalStorage();
                return;
            }
        }
    }

    // returns the total qty of the I-Code in the shopping across across all products and package items
    getICodeQtyInCart(icode: string) {
        let qtyInCartFromOtherItems = 0;
        const shoppingCartItems = this.shoppingCartItemsState.get();
        for (const item of shoppingCartItems) {
            if (item.product.InventoryNo === icode) {
                qtyInCartFromOtherItems += item.qty;
            }
            for (const packageItem of (item.product.PackageItems ?? [])) {
                if (packageItem.ICode === icode) {
                    qtyInCartFromOtherItems += item.packageItemQtys[packageItem.InventoryId] ?? 0;
                }
            }
        }
        return qtyInCartFromOtherItems;
    }

    getMaxProductQty(product: Product | ImmutableObject<Product>) {
        let maxQty = undefined;
        if (product.Availability !== undefined) {
            let icodeQtyInCart = 0;
            let thisProductQtyInCart = 0;
            if ((this.storefrontSettings.get().settings?.SeparateQuoteRequestsByCatalog ?? false) === false) {
                icodeQtyInCart = this.getICodeQtyInCart(product.InventoryNo);
                thisProductQtyInCart = this.getQty(product);
            }
            maxQty = product.Availability.TotalIn - icodeQtyInCart + thisProductQtyInCart;
        }
        return maxQty;
    }

    getMaxPackageItemQty(product: Product | ImmutableObject<Product>, packageItem: PackageItem | ImmutableObject<PackageItem>) {
        let maxQty = undefined;
        if (packageItem.Availability !== undefined) {
            let icodeQtyInCart = 0;
            let thisPackageQtyInCart = 0;
            if ((this.storefrontSettings.get().settings?.SeparateQuoteRequestsByCatalog ?? false) === false) {
                icodeQtyInCart = this.getICodeQtyInCart(packageItem.ICode);
                thisPackageQtyInCart = this.getPackageItemQty(product, packageItem);
            }
            maxQty = packageItem.Availability.TotalIn - icodeQtyInCart + thisPackageQtyInCart; 
        }
        return maxQty;
    }

    setShowPackageItems(shoppingCartItem: ShoppingCartItemModel, showPackageItems: boolean) {
        let shoppingCartItems = JSON.parse(JSON.stringify(this.shoppingCartItemsState.get())) as ShoppingCartItemModel[];
        let shoppingCartItemIndex = shoppingCartItems.findIndex(i => i.product.Id === shoppingCartItem.product.Id);
        if (shoppingCartItemIndex !== -1) {
            const shoppingCartItem = shoppingCartItems[shoppingCartItemIndex];
            if (shoppingCartItem) {
                shoppingCartItem.showPackageItems = showPackageItems;
                this.shoppingCartItemsState.set(shoppingCartItems);
                this.saveToLocalStorage();
            }
        }
    }

    getRates(shoppingCartItem: ShoppingCartItemModel) {
        let dailyRate = shoppingCartItem.product.DailyRate;
        let weeklyRate = shoppingCartItem.product.WeeklyRate;
        let monthlyRate = shoppingCartItem.product.MonthlyRate;
        let salesPrice = shoppingCartItem.product.SalesPrice;
        if ((shoppingCartItem.packageItemQtys !== undefined) && (Object.keys(shoppingCartItem.packageItemQtys).length > 0)) {
            dailyRate = 0;
            weeklyRate = 0;
            monthlyRate = 0;
            for (let inventoryId in shoppingCartItem.packageItemQtys) {
                if (shoppingCartItem.product.PackageItems) {
                    const packageItem = shoppingCartItem.product.PackageItems.find(pi => pi.InventoryId === inventoryId);
                    const qty = shoppingCartItem.packageItemQtys[inventoryId];
                    if ((packageItem !== undefined) && (qty !== undefined) /*&& (packageItem.IsOption)*/) {
                        if (!packageItem.IsOption) {
                            dailyRate += (packageItem.DailyExtended * qty);
                            weeklyRate += (packageItem.WeeklyExtended * qty);
                            monthlyRate += (packageItem.MonthlyExtended * qty);
                        }
                        else {
                            dailyRate += (packageItem.DailyRate * qty);
                            weeklyRate += (packageItem.WeeklyRate * qty);
                            monthlyRate += (packageItem.MonthlyRate * qty);
                        }
                        salesPrice += (packageItem.SalesPrice * qty);
                    }
                }
            }
        }
        return {
            dailyRate: dailyRate,
            weeklyRate: weeklyRate,
            monthlyRate: monthlyRate,
            salesPrice: salesPrice
        }
;
    }

    getRatesByProduct(product?: ImmutableObject<Product>) {
        let rates = {
            dailyRate: 0,
            weeklyRate: 0,
            monthlyRate: 0,
            salesPrice: 0
        };
        if (product) {
            rates = {
                dailyRate: product.DailyRate,
                weeklyRate: product.WeeklyRate,
                monthlyRate: product.MonthlyRate,
                salesPrice: product.SalesPrice
            }
            let shoppingCartItems = JSON.parse(JSON.stringify(this.shoppingCartItemsState.get())) as ShoppingCartItemModel[];
            let shoppingCartItemIndex = shoppingCartItems.findIndex(i => i.product.Id === product.Id);
            if (shoppingCartItemIndex !== -1) {
                const shoppingCartItem = shoppingCartItems[shoppingCartItemIndex];
                rates = this.getRates(shoppingCartItem); 
            }
        }
        return rates;
    }

    private saveToLocalStorage() {
        const obj = this.shoppingCartItemsState.get();
        let json = '';
        if (obj) {
            json = JSON.stringify(obj);
        }
        window.localStorage.setItem(storefront_shoppingcartitems_key, json);
    }

    addMessage(message: ShoppingCartMessage) {
        this.shoppingCartMessages[this.shoppingCartMessages.length].set(message);
    }

    async reloadProductsByIdAsync(productids: string, warehouseid: string, locationid: string, fromDate: string, toDate: string) {
        const productidsList = productids.split(',');
        const axiosRequestConfig: AxiosRequestConfig = {};
        if (this.sessionState.get().loggedIn) {
            axiosRequestConfig.headers = {
                Authorization: 'Bearer ' + this.sessionState.get().authToken
            }
        }
        // load all the products with auto-paging
        const products = await new Promise<Product[]>(async (resolve, reject) => {
            try {
                let more = true;
                const allProducts: Product[] = [];
                let url = new URL(`${this.appConfigState.get().appConfig?.BaseUrl}/api/v1/storefront/products/${productids}/locationid/${locationid}/warehouseid/${warehouseid}/fromdate/${fromDate}/todate/${toDate}`);
                url.searchParams.set("pagesize", "100");
                let pageno = 1;
                while (more) {
                    url.searchParams.set("pageno", pageno.toString());
                    const productsResponse = (await axios.get<FwQueryResponse<Product>>(url.toString(), axiosRequestConfig)).data;
                    allProducts.push(...productsResponse.Items);
                    if (productsResponse.TotalItems === allProducts.length) {
                        more = false;
                    }
                    pageno++;
                }
                resolve(allProducts);
            } catch (error) {
                reject(error);
            }
        });

        // copy the shopping cart
        let shoppingCartItemsCopy = JSON.parse(JSON.stringify(this.shoppingCartItemsState.get())) as ShoppingCartItemModel[];

        // show a message on any products that are no longer in the catalog or unavailable
        const notInCatalogMessage: ShoppingCartMessage = {
            header: 'The following items are no longer available in the catalog or at the selected location.',
            description: 'Please remove the items from your shopping cart.',
            messages: []
        };
        const priceChangedMessage: ShoppingCartMessage = {
            header: 'The following items have changed price.',
            description: 'Items in your shopping cart are updated to reflect the most current price from the product details page.',
            messages: []
        };
        const unavailableMessage: ShoppingCartMessage = {
            header: 'The following items are not available during the requested period.',
            description: '',
            messages: []
        };
        for (let i = 0; i < shoppingCartItemsCopy.length; i++) {
            const shoppingCartItem = shoppingCartItemsCopy[i];
            if (productidsList.indexOf(shoppingCartItem.product.Id) >= 0) {
                const catalog = this.catalogState.getCatalogById(shoppingCartItem.product.WebCatalogId);
                if (catalog && (catalog.LocationId === locationid) && (catalog.WarehouseId === warehouseid)) {
                    shoppingCartItem.hasQtyError = false;
                    const product = products.find(p => p.Id === shoppingCartItem.product.Id);
                    if (product === undefined) {
                        notInCatalogMessage.messages.push(`${shoppingCartItem.product.ItemName}`);
                        //shoppingCartItem.hasQtyError = true;
                    }
                    else {
                        await this.catalogState.loadAsync();
                        const catalog = this.catalogState.getProductCatalog(product);
                        let allowAddingUnavailableItems = catalog?.AllowAddingUnavailableItems ?? false;
                        const totalIn = product.Availability?.TotalIn ?? 0;
                        if ((!allowAddingUnavailableItems) && (product.Availability !== undefined) && (totalIn <= 0)) {
                            unavailableMessage.messages.push(`${shoppingCartItem.product.ItemName}`);
                            shoppingCartItem.hasQtyError = true;
                        }
                        if (product.DailyRate !== shoppingCartItem.product.DailyRate) {
                            priceChangedMessage.messages.push(`${shoppingCartItem.product.ItemName} DailyRate for ${product.DailyRate > shoppingCartItem.product.DailyRate ? 'increased' : 'decreased'} from ${shoppingCartItem.product.CurrencySymbol} ${shoppingCartItem.product.DailyRate} to ${product.CurrencySymbol} ${product.DailyRate}`);
                        }
                        if (product.WeeklyRate !== shoppingCartItem.product.WeeklyRate) {
                            priceChangedMessage.messages.push(`${shoppingCartItem.product.ItemName} WeeklyRate for ${product.WeeklyRate > shoppingCartItem.product.WeeklyRate ? 'increased' : 'decreased'} from ${shoppingCartItem.product.CurrencySymbol} ${shoppingCartItem.product.WeeklyRate} to ${product.CurrencySymbol} ${product.WeeklyRate}`);
                        }
                        if (product.MonthlyRate !== shoppingCartItem.product.MonthlyRate) {
                            priceChangedMessage.messages.push(`${shoppingCartItem.product.ItemName} MonthlyRate for ${product.MonthlyRate > shoppingCartItem.product.MonthlyRate ? 'increased' : 'decreased'} from ${shoppingCartItem.product.MonthlyRate} ${shoppingCartItem.product.MonthlyRate} to ${product.CurrencySymbol} ${product.MonthlyRate}`);
                        }
                    }
                }
            }
        }
        const deletions: { [key: number]: any } = {};
        for (let i = 0; i < this.shoppingCartMessages.length; i++) {
            deletions[i] = none;
        }
        this.shoppingCartMessages.merge(deletions);
        if (notInCatalogMessage.messages.length > 0) {
            this.shoppingCartMessages[this.shoppingCartMessages.length].set(notInCatalogMessage);
        }
        if (unavailableMessage.messages.length > 0) {
            this.shoppingCartMessages[this.shoppingCartMessages.length].set(unavailableMessage);
        }
        if (priceChangedMessage.messages.length > 0) {
            this.shoppingCartMessages[this.shoppingCartMessages.length].set(priceChangedMessage);
        }

        // update the product for shopping cart items.
        for (let i = 0; i < products.length; i++) {
            const product = products[i];
            const shoppingCartItem = shoppingCartItemsCopy.find(c => c.product.Id === product.Id);
            if (shoppingCartItem) {
                shoppingCartItem.product = product;
            }
        }

        this.shoppingCartItemsState.set(shoppingCartItemsCopy);
        this.saveToLocalStorage();
    }

    /**
     * Reload the product info that's saved in localStorage on the shopppingCart items, validate that the user is allowed to have the items in their cart, 
     * and set reactive state for the shopping cart messages.
     */
    async reloadProductsAsync(warehouseid: string, locationid: string, fromDate: string, toDate: string) {
        const isUniversity = this.storefrontSettings.get().settings?.IsUniversity ?? false;

        // reset all shopping cart qty errors
        //const items = clone<ShoppingCartItemModel[]>(this.shoppingCartItemsState.get() as any);
        //for (let i = 0; i < items.length; i++) {
        //    let item = items[i];
        //    items[i] = {
        //        ...items[i],
        //        hasQtyError: false,
        //    }
        //    this.shoppingCartItemsState.set(items);
        //}

        if (isUniversity) {
            // build a list of catalogs so we we can call the products endpoint once for each catalog that's in the shopping cart.
            let catalogs: Catalog[] = [];
            let items = this.shoppingCartItemsState.get();
            for (let item of items) {
                const webCatalogId = item.product.WebCatalogId;
                const catalog = catalogs?.find(c => c?.Id === webCatalogId);
                if (!catalog) {
                    const catalog = this.catalogState.getCatalogById(webCatalogId);
                    catalogs.push(catalog as Catalog);
                }
            }
            for (const catalog of catalogs) {
                let productids: string = '';
                const shoppingCartItems = this.get();
                for (var shoppingCartItem of shoppingCartItems) {
                    if (shoppingCartItem.product.WebCatalogId === catalog.Id) {
                        if (productids.length > 0) {
                            productids += ',';
                        }
                        productids += shoppingCartItem.product.Id
                    }
                }
                await this.reloadProductsByIdAsync(productids, catalog.WarehouseId, catalog.LocationId, fromDate, toDate);
            }
        }
        else if (warehouseid && locationid) {
            let productids: string = '';
            const shoppingCartItems = this.get();
            for (var shoppingCartItem2 of shoppingCartItems) {
                if (productids.length > 0) {
                    productids += ',';
                }
                productids += shoppingCartItem2.product.Id
            }
            await this.reloadProductsByIdAsync(productids, warehouseid, locationid, fromDate, toDate);
        }
    }

    hasCatalogTypes() { 
        const shoppingCartItems = this.shoppingCartItemsState.get();
        let hasRental = false;
        let hasSales = false;
        for (let i = 0; i < shoppingCartItems.length; i++) {
            const shoppingCartItem = shoppingCartItems[i];
            const catalog = this.catalogState.getCatalogById(shoppingCartItem.product.WebCatalogId);
            if (catalog?.HasRental) {
                hasRental = true;
            }
            if (catalog?.HasSales) {
                hasSales = true;
            }
        }
        return {
            hasRental,
            hasSales,
        }
    }
}

export function useShoppingCartItemsState(): ShoppingCartItemsState {
    const localeState = useLocaleState();
    const shoppingCartItemsState = useHookstate<ShoppingCartItemModel[]>(shoppingCartItemsStateStore);
    const catalogState = useCatalogState();
    const appConfigState = useAppConfigState();
    const sessionState = useSessionState();
    const shoppingCartMessages = useHookstate<ShoppingCartMessage[]>([]);
    const storefrontSettings = useStorefrontSettingsState();
    return new ShoppingCartItemsState(localeState, shoppingCartItemsState, catalogState, appConfigState, sessionState, shoppingCartMessages, storefrontSettings);
}
